import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';
import { AuthProvider } from './components/self-service/auth/useAuth';
import DevMenu from './components/self-service/DevMenu';
import HelpMenu from './components/self-service/help/HelpMenu';
import AdminShell from './components/self-service/admin/AdminShell';
import BrowseChannelsPage from './pages/self-service/BrowseChannelsPage';
import SelfServicePage from './pages/self-service/SelfServicePage';
import SubmittedPage from './pages/self-service/SubmittedPage';
import MyCampaignsPage from './pages/self-service/MyCampaignsPage';
import AdvertiserCampaignDetailPage from './pages/self-service/AdvertiserCampaignDetailPage';
import CampaignReportPage from './pages/self-service/CampaignReportPage';
import UpdatePaymentPage from './pages/self-service/UpdatePaymentPage';
import AdvertiserCreativeLibraryPage from './pages/self-service/AdvertiserCreativeLibraryPage';
import PublisherAdminPage from './pages/self-service/PublisherAdminPage';
import PublisherPaymentsPage from './pages/self-service/PublisherPaymentsPage';
import PublisherBillingPage from './pages/self-service/PublisherBillingPage';
import PublisherSubmissionsPage from './pages/self-service/PublisherSubmissionsPage';
import PublisherSubmissionDetailPage from './pages/self-service/PublisherSubmissionDetailPage';
import PublisherAdvertisersPage from './pages/self-service/PublisherAdvertisersPage';
import PublisherReferralsPage from './pages/self-service/PublisherReferralsPage';
import PublisherReferralDetailPage from './pages/self-service/PublisherReferralDetailPage';
import PublisherMyReferralsPage from './pages/self-service/PublisherMyReferralsPage';
import PublisherPromotionsPage from './pages/self-service/PublisherPromotionsPage';
import PublisherMaterialsPage from './pages/self-service/PublisherMaterialsPage';
import PublisherPromotionDetailPage from './pages/self-service/PublisherPromotionDetailPage';
import PublisherChannelSettingsPage from './pages/self-service/PublisherChannelSettingsPage';
import PublisherOnboardPage from './pages/self-service/PublisherOnboardPage';
import PublisherPublicaPushJobsPage from './pages/self-service/PublisherPublicaPushJobsPage';
import PublisherPublicaUploadsPage from './pages/self-service/PublisherPublicaUploadsPage';
import WaymarkCredentialsPage from './pages/self-service/WaymarkCredentialsPage';
import EmailTemplatesPage from './pages/self-service/EmailTemplatesPage';
import StudioPage from './pages/self-service/StudioPage';
import PublisherPublicaPushJobDetailPage from './pages/self-service/PublisherPublicaPushJobDetailPage';
import AdminHelpCenterPage from './pages/self-service/AdminHelpCenterPage';
import HelpCenterPage from './pages/self-service/HelpCenterPage';
import InvitationLandingPage from './pages/self-service/InvitationLandingPage';
import PublisherTeamPage from './pages/self-service/PublisherTeamPage';
import PublisherTeamRedirectPage from './pages/self-service/PublisherTeamRedirectPage';
import PublisherSettingsLayout from './pages/self-service/PublisherSettingsLayout';
import PublisherPayoutMethodPage from './pages/self-service/PublisherPayoutMethodPage';
import StationInviteAcceptPage from './pages/self-service/StationInviteAcceptPage';
import PublisherIntakePage from './pages/self-service/PublisherIntakePage';
import AccessDeniedPage from './pages/self-service/AccessDeniedPage';
import ResetPasswordPage from './pages/self-service/ResetPasswordPage';
import ImpersonatePage from './pages/self-service/ImpersonatePage';
import AdminEntryPage from './pages/self-service/AdminEntryPage';
import AdvertiserTermsPage from './pages/self-service/AdvertiserTermsPage';
import SettingsLayout from './pages/settings/SettingsLayout';
import SettingsBusinessTab from './pages/settings/SettingsBusinessTab';
import SettingsTeamTab from './pages/settings/SettingsTeamTab';
import SettingsSecurityTab from './pages/settings/SettingsSecurityTab';
import * as analytics from './lib/analytics';
import { isSelfServiceDevelopment } from './lib/env';
import './index.css';

const wrap = (el: React.ReactNode) => (
  <AuthProvider>
    {el}
    <HelpMenu />
    <DevMenu />
  </AuthProvider>
);

// Redirect legacy /:slug/my-team bookmarks to the consolidated Settings ->
// Team tab. Uses useParams so the slug survives into the Navigate target.
// eslint-disable-next-line react-refresh/only-export-components -- routing indirection, not a shared component
const MyTeamLegacyRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/${slug}/settings/team` : '/'} replace />;
};

// Analytics boot MUST precede router creation. Both providers read the full
// landing URL (GA4 from page_location on the first event, posthog-js from the
// live URL), then the utm_* params are pinned to the PostHog session and
// scrubbed from the address bar. Doing this before createBrowserRouter matters:
// the router snapshots the location at creation, and any later setSearchParams
// (e.g. SelfServicePage consuming ?resume=) would write the stale, still-
// tagged snapshot back into the URL, undoing the scrub.
analytics.init();
let lastTrackedPath = window.location.pathname;
analytics.page(lastTrackedPath);
analytics.captureAndStripCampaignParams();

const router = createBrowserRouter([
  // Closed-loop per-station advertiser dashboard. Legacy /my-campaigns(/...)
  // URLs from old emails redirect to root (BrowseChannels), which sends the
  // user back into a station context.
  { path: '/:slug/my-campaigns', element: wrap(<MyCampaignsPage />) },
  { path: '/:slug/my-campaigns/:submissionId/update-payment', element: wrap(<UpdatePaymentPage />) },
  { path: '/:slug/my-campaigns/:submissionId', element: wrap(<AdvertiserCampaignDetailPage />) },
  // CTV-445: dedicated performance report (delivery + geo + daypart + creative).
  { path: '/:slug/my-campaigns/:submissionId/report', element: wrap(<CampaignReportPage />) },
  { path: '/my-campaigns', element: <Navigate to="/" replace /> },
  { path: '/my-campaigns/:submissionId', element: <Navigate to="/" replace /> },
  // CTV-457 Phase B: cross-station Library of commercials the user has
  // submitted. Lives under :slug so the in-loop chrome (PublicHeader, sign-
  // out fallback) renders with station branding even though the underlying
  // data is per-user across all stations.
  { path: '/:slug/library', element: wrap(<AdvertiserCreativeLibraryPage />) },
  // Consolidated advertiser Settings surface (business / team / security).
  // SettingsLayout owns the PublicHeader + tab strip; each sub-route
  // renders as an <Outlet /> inside it.
  {
    path: '/:slug/settings',
    element: wrap(<SettingsLayout />),
    children: [
      { index: true, element: <Navigate to="business" replace /> },
      { path: 'business', element: <SettingsBusinessTab /> },
      { path: 'team', element: <SettingsTeamTab /> },
      { path: 'security', element: <SettingsSecurityTab /> },
    ],
  },
  // Legacy /my-team bookmarks land on the new Team tab.
  { path: '/:slug/my-team', element: <MyTeamLegacyRedirect /> },
  { path: '/my-team', element: <Navigate to="/" replace /> },
  // Pre-consolidation Security URL is unrouted without a slug — bounce home.
  { path: '/settings/security', element: <Navigate to="/" replace /> },
  { path: '/invitations/:token', element: wrap(<InvitationLandingPage />) },
  { path: '/station-invites/:token', element: wrap(<StationInviteAcceptPage />) },
  // Publisher self-onboarding intake. Public: the HMAC token in the path is
  // the capability. Static `onboard` segment outranks the `/:slug` wizard.
  { path: '/onboard/:token', element: wrap(<PublisherIntakePage />) },
  { path: '/access-denied', element: wrap(<AccessDeniedPage />) },
  { path: '/reset-password', element: wrap(<ResetPasswordPage />) },
  // CTV-760: super-admin "Login As -> Self-Serve" handoff from the dashboard.
  // Redeems the magic-link token in the URL fragment, then forwards to /admin.
  // Renders without wrap() so no AuthProvider hydrates against the outgoing
  // session mid-swap. /admin mounts its own once the new session exists.
  { path: '/impersonate', element: <ImpersonatePage /> },
  // CTV-639: publisher-neutral CTVBuyer Advertiser Terms. No auth or station
  // context needed — it's the same platform-level document for every buyer, so
  // it lives at a global path (not under /:slug) and renders without wrap().
  { path: '/advertiser-terms', element: <AdvertiserTermsPage /> },

  // Staff admin. /admin = entry/picker. /admin/onboard = super_admin channel
  // creation (top-level — no station context). /admin/:slug = per-station
  // shell with all sub-views as nested children.
  //
  // CTV-759 / ADR-0036: /admin/all is the All-stations sentinel and is served
  // by the SAME /admin/:slug route element — AdminShell branches on the slug
  // before its gate. Deliberately not a sibling top-level route: wrap()
  // mounts AuthProvider per top-level route, so a sibling would remount auth
  // and refire /stations/my-admin, /memberships/mine and the grant RPC on
  // every scope change. `all` is reserved in validateSlug so no station can
  // ever claim it.
  { path: '/admin', element: wrap(<AdminEntryPage />) },
  { path: '/admin/onboard', element: wrap(<PublisherOnboardPage />) },
  { path: '/admin/publica-push-jobs', element: wrap(<PublisherPublicaPushJobsPage />) },
  // CTV-732: the super-admin Publica Upload Task worklist.
  { path: '/admin/publica-uploads', element: wrap(<PublisherPublicaUploadsPage />) },
  { path: '/admin/waymark-credentials', element: wrap(<WaymarkCredentialsPage />) },
  // CTV-784: super-admin templated publisher emails (fill in + copy, no sending).
  { path: '/admin/email-templates', element: wrap(<EmailTemplatesPage />) },
  // CTV-800: the staff Waymark studio. Top-level like the other static
  // /admin segments, so `studio` is reserved in RESERVED_STATION_SLUGS and no
  // station can shadow it. It is also the sentinel station slug every studio
  // commercial carries, which is what routes them to CTVBuyer's own Waymark
  // partner.
  { path: '/admin/studio', element: wrap(<StudioPage />) },
  { path: '/admin/publica-push-jobs/:jobId', element: wrap(<PublisherPublicaPushJobDetailPage />) },
  { path: '/admin/help-center', element: wrap(<AdminHelpCenterPage />) },
  {
    path: '/admin/:slug',
    element: wrap(<AdminShell />),
    children: [
      { index: true, element: <PublisherAdminPage /> },
      // CTV-776: Billing = the payout ledger. CTV-787 moved the Stripe payout
      // setup out to Station settings, so `payments` redirects there and
      // Billing keeps the ledger alone.
      { path: 'billing', element: <PublisherBillingPage /> },
      { path: 'payments', element: <PublisherPaymentsPage /> },
      // CTV-787: Station settings owns the header, the tab strip and the
      // manager+ gate; each tab is a sections-only page inside its Outlet.
      {
        path: 'settings',
        element: <PublisherSettingsLayout />,
        children: [
          { index: true, element: <Navigate to="team" replace /> },
          { path: 'team', element: <PublisherTeamPage /> },
          { path: 'payout-method', element: <PublisherPayoutMethodPage /> },
        ],
      },
      { path: 'submissions', element: <PublisherSubmissionsPage /> },
      { path: 'submissions/:submissionId', element: <PublisherSubmissionDetailPage /> },
      { path: 'advertisers', element: <PublisherAdvertisersPage /> },
      { path: 'team', element: <PublisherTeamRedirectPage /> },
      { path: 'referrals', element: <PublisherReferralsPage /> },
      { path: 'referrals/:salespersonId', element: <PublisherReferralDetailPage /> },
      // CTV-673: the rep-scoped stats view (self-only data at the API).
      { path: 'my-referrals', element: <PublisherMyReferralsPage /> },
      { path: 'promotions', element: <PublisherPromotionsPage /> },
      { path: 'materials', element: <PublisherMaterialsPage /> },
      { path: 'promotions/:promotionId', element: <PublisherPromotionDetailPage /> },
      { path: 'channel-settings', element: <PublisherChannelSettingsPage /> },
    ],
  },

  // Public + advertiser-facing wizard routes.
  // CTV-593: the index route (BrowseChannels) is dev-only. Prod is white-
  // labeled, so users only ever enter through /{station}. Hitting `/` in
  // prod falls through to React Router's default no-match (404).
  ...(isSelfServiceDevelopment()
    ? [{ path: '/', element: wrap(<BrowseChannelsPage />) }]
    : []),
  { path: '/:slug', element: wrap(<SelfServicePage />) },
  { path: '/:slug/start', element: wrap(<SelfServicePage />) },
  { path: '/:slug/start/:step', element: wrap(<SelfServicePage />) },
  { path: '/:slug/submitted/:submissionId', element: wrap(<SubmittedPage />) },
  { path: '/:slug/help', element: wrap(<HelpCenterPage />) },
]);

// SPA pageview on every route change. router.subscribe doesn't emit for the
// initial load; that one was sent in the analytics boot above.
router.subscribe((state) => {
  if (state.navigation.state === 'idle' && state.location.pathname !== lastTrackedPath) {
    lastTrackedPath = state.location.pathname;
    analytics.page(lastTrackedPath);
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
